<template>
  <div class="add-booking-page">
    <div class="page-header">
      <h1 class="page-title">{{ $t('booking.addBooking') }}</h1>
    </div>

    <el-card class="booking-form-card" shadow="never">
      <!-- Teacher Selection -->
      <div class="form-section">
        <label class="field-label">
          {{ $t('booking.teacher') }} <span class="required">*</span>
        </label>
        <el-select
          v-model="bookingForm.teacherId"
          :placeholder="$t('booking.selectTeacher')"
          class="full-width select-with-icon"
          filterable
          :filter-method="filterTeachers"
          @change="handleTeacherChange"
        >
          <template #prefix>
            <i class="iconfont icon-teacher select-icon"></i>
          </template>
          <el-option
            v-for="teacher in filteredTeachers"
            :key="teacher.id"
            :label="teacher.name"
            :value="teacher.id"
          >
            <span class="option-content">
              <span class="option-name">{{ teacher.name }}</span>
              <span class="option-phone">{{ teacher.phone }}</span>
            </span>
          </el-option>
        </el-select>
      </div>

      <!-- Customer Selection -->
      <div class="form-section">
        <label class="field-label">
          {{ $t('booking.customer') }} <span class="required">*</span>
        </label>
        <el-select
          v-model="bookingForm.customerId"
          :placeholder="$t('booking.selectCustomer')"
          class="full-width select-with-icon"
          filterable
          :filter-method="filterCustomers"
          @change="handleCustomerChange"
        >
          <template #prefix>
            <i class="iconfont icon-helmet select-icon"></i>
          </template>
          <el-option
            v-for="customer in filteredCustomers"
            :key="customer.id"
            :label="customer.name"
            :value="customer.id"
          >
            <span class="option-content">
              <span class="option-name">{{ customer.name }}</span>
              <span class="option-phone">{{ customer.phone }}</span>
            </span>
          </el-option>
        </el-select>
      </div>

      <!-- Date Selection -->
      <div class="form-section" v-if="bookingForm.teacherId">
        <label class="field-label">
          {{ $t('booking.date') }} <span class="required">*</span>
        </label>
        <div class="available-dates-container">
          <div class="dates-header">{{ $t('booking.availableDates') }}</div>
          
          <!-- Loading state -->
          <div v-if="loadingSchedule" class="schedule-loading">
            <el-icon class="is-loading"><Loading /></el-icon>
            <span>{{ $t('booking.loadingSchedule') }}</span>
          </div>
          
          <!-- Dates grid -->
          <div v-else class="available-dates">
            <div
              v-for="date in availableDates"
              :key="date.value"
              :class="['date-item', { 
                'is-selected': bookingForm.date === date.value,
                'is-disabled': !date.available
              }]"
              @click="selectDate(date.value)"
            >
              <div class="date-value">{{ date.label }}</div>
              <div class="date-weekday">{{ date.weekday }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Hours Selection -->
      <div class="form-section" v-if="bookingForm.date">
        <label class="field-label">
          {{ $t('booking.selectHours') }} <span class="required">*</span>
        </label>
        <div class="hours-grid">
          <div
            v-for="hour in availableHours"
            :key="hour.value"
            :class="['hour-item', { 
              'is-selected': selectedHours.includes(hour.value),
              'is-disabled': !hour.available,
              'is-booked': hour.booked
            }]"
            @click="toggleHour(hour.value)"
          >
            <span class="hour-label">{{ hour.label }}</span>
            <el-icon v-if="!hour.available && !hour.booked" class="unavailable-icon"><Close /></el-icon>
            <el-icon v-if="hour.booked" class="booked-icon"><Clock /></el-icon>
          </div>
        </div>
        <div class="hours-hint">
          {{ $t('booking.selectAtLeastOneHour') }}
        </div>
      </div>

      <!-- VIP Customer Info -->
      <div class="form-section" v-if="bookingForm.customerId">
        <label class="field-label">{{ $t('booking.vipInfo') }}</label>
        <div class="vip-info-display" :class="{ 
          'vip-warning': vipInfo.hasVip && vipInfo.remainingHours < 5,
          'vip-error': vipInfo.hasVip && (vipInfo.remainingHours <= 0 || selectedHours.length > vipInfo.remainingHours),
          'vip-blocked': !vipInfo.hasVip
        }">
          <el-icon class="vip-icon"><Trophy /></el-icon>
          <div class="vip-info-content">
            <span class="vip-status" v-if="vipInfo.hasVip">
              {{ $t('booking.vipActive') }} - {{ vipInfo.packageName }}
            </span>
            <span class="vip-status" v-else>
              {{ $t('booking.notVip') }}
            </span>
            <span class="vip-hours" v-if="vipInfo.hasVip">
              {{ $t('booking.remainingHours') }}: {{ vipInfo.remainingHours }} / {{ vipInfo.totalHours }}
            </span>
            <span class="vip-last-updated" v-if="vipInfo.hasVip && vipInfo.lastUpdated">
              🕒 {{ $t('booking.lastUpdated') }}: {{ formatLastUpdated(vipInfo.lastUpdated) }}
            </span>
            <span class="vip-warning-text" v-if="vipInfo.hasVip && vipInfo.remainingHours <= 0">
              ⚠️ {{ $t('booking.vipNoHours') }}
            </span>
            <span class="vip-warning-text" v-if="vipInfo.hasVip && selectedHours.length > vipInfo.remainingHours">
              ⚠️ {{ $t('booking.vipHoursExceed') }}
            </span>
            <span class="vip-warning-text" v-if="!vipInfo.hasVip" style="color: #f56c6c; font-weight: 600;">
              ⚠️ {{ $t('booking.onlyVipCanBook') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="form-actions">
        <el-button class="cancel-btn" @click="handleCancel">
          {{ $t('common.cancel') }}
        </el-button>
        <el-button
          type="primary"
          class="confirm-btn"
          :disabled="!canSubmit"
          @click="goToConfirm"
        >
          <el-icon><ArrowRight /></el-icon>
          {{ $t('booking.confirm.continue') }}
        </el-button>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { ArrowRight, School, Star, Close, Trophy, Clock, Loading } from '@element-plus/icons-vue'
import { useBookingStore } from '@/stores/booking'
import { useCustomerStore } from '@/stores/customer'
import { useTeacherStore } from '@/stores/teacher'
import { useScheduleStore } from '@/stores/schedule'
import { getCustomerRemainingHours } from '@/api/vip'
import { getWeeklyCalendarTemplates } from '@/api/schedule'
import dayjs from 'dayjs'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const bookingStore = useBookingStore()
const customerStore = useCustomerStore()
const teacherStore = useTeacherStore()
const scheduleStore = useScheduleStore()

const loading = ref(false)
const loadingSchedule = ref(false)
const teachers = ref([])
const customers = ref([])
const filteredTeachers = ref([])
const filteredCustomers = ref([])
const teacherSearch = ref('')
const customerSearch = ref('')

// Filter functions for select dropdowns
const filterTeachers = (query) => {
  teacherSearch.value = query.toLowerCase()
  if (!query) {
    filteredTeachers.value = teachers.value
  } else {
    filteredTeachers.value = teachers.value.filter(t => 
      t.name.toLowerCase().includes(query) || 
      (t.phone && t.phone.includes(query))
    )
  }
}

const filterCustomers = (query) => {
  customerSearch.value = query.toLowerCase()
  if (!query) {
    filteredCustomers.value = customers.value
  } else {
    filteredCustomers.value = customers.value.filter(c => 
      c.name.toLowerCase().includes(query) || 
      (c.phone && c.phone.includes(query))
    )
  }
}
const availableDates = ref([])
const availableHours = ref([])
const selectedHours = ref([])
const weeklyCalendarTemplates = ref([]) // Store weekly calendar templates
const vipInfo = reactive({
  hasVip: false,
  packageName: '',
  remainingHours: 0,
  totalHours: 0,
  lastUpdated: ''
})
const pendingRestore = ref(false)

const bookingForm = reactive({
  teacherId: '',
  customerId: '',
  date: '',
  hours: []
})

// Computed properties
const canSubmit = computed(() => {
  const hasBasicRequirements = (
    bookingForm.teacherId &&
    bookingForm.customerId &&
    bookingForm.date &&
    selectedHours.value.length > 0
  )
  
  if (!hasBasicRequirements) {
    return false
  }
  
  // CRITICAL: ONLY VIP customers can book
  if (!vipInfo.hasVip) {
    return false // Non-VIP cannot book
  }
  
  // VIP validation
  if (vipInfo.hasVip) {
    const selectedHoursCount = selectedHours.value.length
    
    // Cannot submit if VIP has no hours or selected hours exceed remaining
    if (vipInfo.remainingHours <= 0 || selectedHoursCount > vipInfo.remainingHours) {
      return false
    }
  }
  
  return true
})

// Load teachers
const loadTeachers = async () => {
  try {
    await teacherStore.fetchTeachers()
    // Filter only active teachers (status=1)
    teachers.value = teacherStore.teachers.filter(t => t.status === 1)
    filteredTeachers.value = [...teachers.value]
  } catch (error) {
    ElMessage.error(t('booking.loadFailed'))
  }
}

// Load customers
const loadCustomers = async () => {
  try {
    await customerStore.fetchCustomers()
    // Filter only active customers (status=1)
    customers.value = customerStore.customers.filter(c => c.status === 1)
    filteredCustomers.value = [...customers.value]
  } catch (error) {
    ElMessage.error(t('booking.loadFailed'))
  }
}

// Load weekly calendar templates
const loadWeeklyCalendarTemplates = async () => {
  try {
    const res = await getWeeklyCalendarTemplates()
    if (res.success && res.data) {
      weeklyCalendarTemplates.value = res.data
      console.log('[Booking Add] Weekly calendar templates loaded:', weeklyCalendarTemplates.value)
    }
  } catch (error) {
    console.error('[Booking Add] Failed to load weekly calendar templates:', error)
  }
}

// Load VIP info for selected customer
const loadVipInfo = async (customerId) => {
  try {
    // Use dedicated API to get remaining hours
    const response = await getCustomerRemainingHours(customerId)
    const data = response.data
    
    if (data && data.totalRemainingHours > 0) {
      vipInfo.hasVip = true
      vipInfo.packageName = data.activePackageCount > 0 ? 'VIP Package' : ''
      vipInfo.remainingHours = data.totalRemainingHours || 0
      vipInfo.totalHours = data.totalRemainingHours || 0
      vipInfo.lastUpdated = data.lastUpdated || ''
    } else {
      vipInfo.hasVip = false
      vipInfo.remainingHours = 0
      vipInfo.totalHours = 0
      vipInfo.lastUpdated = ''
    }
  } catch (error) {
    console.error('Failed to load VIP info:', error)
    vipInfo.hasVip = false
    vipInfo.remainingHours = 0
    vipInfo.totalHours = 0
    vipInfo.lastUpdated = ''
  }
}

// Handle teacher selection change
const handleTeacherChange = async () => {
  bookingForm.date = ''
  selectedHours.value = []
  availableDates.value = []
  availableHours.value = []
  
  if (bookingForm.teacherId) {
    console.log('[Booking Add] Teacher selected, fetching schedule for teacherId:', bookingForm.teacherId)
    loadingSchedule.value = true
    
    try {
      // Fetch teacher's weekly schedule and wait for it to complete
      await scheduleStore.fetchWeeklySchedule(bookingForm.teacherId)
      loadAvailableDates()
    } catch (error) {
      console.error('[Booking Add] Failed to load schedule:', error)
      ElMessage.error(t('booking.loadFailed'))
    } finally {
      loadingSchedule.value = false
    }
  }
}

// Handle customer selection change
const handleCustomerChange = () => {
  if (bookingForm.customerId) {
    loadVipInfo(bookingForm.customerId)
  } else {
    vipInfo.hasVip = false
  }
}

// Load available dates for selected teacher
const loadAvailableDates = async () => {
  try {
    // Fetch schedule for the teacher
    const today = dayjs()
    const dates = []
    
    // Generate next 7 days
    for (let i = 0; i < 7; i++) {
      const date = dayjs().add(i, 'day')
      dates.push({
        value: date.format('YYYY-MM-DD'),
        label: date.format('MM.DD'),
        weekday: getWeekdayLabel(date.day()),
        available: true // TODO: Check actual availability from schedule
      })
    }
    
    availableDates.value = dates
  } catch (error) {
    console.error('Failed to load dates:', error)
  }
}

// Get weekday label in Chinese
const getWeekdayLabel = (day) => {
  const weekdays = ['日', '一', '二', '三', '四', '五', '六']
  return `(周${weekdays[day]})`
}

// Format last updated timestamp
const formatLastUpdated = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Select date
const selectDate = (dateValue) => {
  bookingForm.date = dateValue
  selectedHours.value = []
  loadAvailableHours()
}

// Load available hours for selected date
const loadAvailableHours = async () => {
  try {
    // Find the schedule entry for the selected date from weekly schedule API
    const scheduleEntry = scheduleStore.weeklySchedule?.find(item => {
      const itemDate = dayjs(item.date).format('YYYY-MM-DD')
      return itemDate === bookingForm.date
    })
    
    let availableHourList = []
    
    if (scheduleEntry && scheduleEntry.avalableHours && Array.isArray(scheduleEntry.avalableHours)) {
      // Use the avalableHours from the weekly schedule API response
      console.log('[Booking Add] Using avalableHours from API for date:', bookingForm.date)
      console.log('[Booking Add] avalableHours:', scheduleEntry.avalableHours)
      
      // Extract all hours from avalableHours array
      availableHourList = scheduleEntry.avalableHours.map(slot => slot.hour)
    } else {
      // Fallback: if no schedule found, use all hours from 08:00 to 23:00
      console.log('[Booking Add] No schedule found for date', bookingForm.date, ', using fallback')
      availableHourList = Array.from({ length: 16 }, (_, i) => i + 8) // [8, 9, 10, ..., 23]
    }
    
    // Get unavailable and booked hours from the schedule
    const { unavailableHours, bookedHours } = getUnavailableAndBookedHoursForDate(bookingForm.date)
    
    // Generate hours based on the API data
    const hours = []
    availableHourList.forEach(hourNum => {
      const hourValue = `${hourNum.toString().padStart(2, '0')}:00`
      const isUnavailable = unavailableHours.includes(hourValue)
      const isBooked = bookedHours.includes(hourValue)
      hours.push({
        value: hourValue,
        label: hourValue,
        available: !isUnavailable, // Booked hours are now available
        booked: isBooked // Track for visual indicator only
      })
    })
    availableHours.value = hours
    
    console.log('[Booking Add] Generated hours:', hours)
    console.log('[Booking Add] Unavailable hours:', unavailableHours)
    console.log('[Booking Add] Booked hours:', bookedHours)
  } catch (error) {
    console.error('Failed to load hours:', error)
  }
}

// Get unavailable and booked hours for a specific date from weekly schedule
const getUnavailableAndBookedHoursForDate = (dateStr) => {
  if (!scheduleStore.weeklySchedule || scheduleStore.weeklySchedule.length === 0) {
    return { unavailableHours: [], bookedHours: [] }
  }
  
  // Find the schedule entry for the selected date
  const scheduleEntry = scheduleStore.weeklySchedule.find(item => {
    const itemDate = dayjs(item.date).format('YYYY-MM-DD')
    return itemDate === dateStr
  })
  
  if (!scheduleEntry) {
    return { unavailableHours: [], bookedHours: [] }
  }
  
  // Extract unavailable and booked hours from the schedule
  const unavailableHours = []
  const bookedHours = []
  
  // Handle the actual API response structure with avalableHours array
  if (scheduleEntry.avalableHours && Array.isArray(scheduleEntry.avalableHours)) {
    scheduleEntry.avalableHours.forEach(hourSlot => {
      const hourValue = `${String(hourSlot.hour).padStart(2, '0')}:00`
      // Status codes:
      // 0 = Available (can book)
      // 1 = Booked (already booked) - cannot select
      // 2 = Blocked (teacher blocked) - cannot select
      // 3 = Cancelled (was booked but cancelled) - can book
      // 4 = Passed (past time) - cannot select
      if (hourSlot.status === 1 || hourSlot.status === 2 || hourSlot.status === 4) {
        unavailableHours.push(hourValue)
      }
      if (hourSlot.status === 1) {
        bookedHours.push(hourValue)
      }
    })
  } else if (scheduleEntry.hourSlots) {
    // If schedule has hourSlots array (fallback)
    scheduleEntry.hourSlots.forEach(slot => {
      const hourValue = `${String(slot.hour).padStart(2, '0')}:00`
      if (slot.status === 'booked' || slot.status === 'unavailable' || slot.status === 'passed') {
        unavailableHours.push(hourValue)
      }
      if (slot.status === 'booked') {
        bookedHours.push(hourValue)
      }
    })
  } else if (scheduleEntry.hours) {
    // If schedule has hours array (fallback)
    console.log('[Booking Add] Using hours:', scheduleEntry.hours)
    scheduleEntry.hours.forEach(hour => {
      const hourValue = `${String(hour.hour).padStart(2, '0')}:00`
      if (hour.status === 'booked' || hour.status === 'unavailable' || hour.status === 'passed') {
        unavailableHours.push(hourValue)
      }
      if (hour.status === 'booked') {
        bookedHours.push(hourValue)
      }
    })
  } else if (scheduleEntry.unavailableHours) {
    // If schedule directly provides unavailableHours array (fallback)
    console.log('[Booking Add] Using unavailableHours:', scheduleEntry.unavailableHours)
    unavailableHours.push(...scheduleEntry.unavailableHours)
  } else {
    // Try to find any array property that might contain hours
    console.log('[Booking Add] Unknown data structure, checking all properties...')
    Object.keys(scheduleEntry).forEach(key => {
      if (Array.isArray(scheduleEntry[key])) {
        console.log('[Booking Add] Found array property:', key, scheduleEntry[key])
      }
    })
  }
  
  console.log('[Booking Add] Final unavailable hours:', unavailableHours)
  console.log('[Booking Add] Final booked hours:', bookedHours)
  return { unavailableHours, bookedHours }
}

// Toggle hour selection
const toggleHour = (hourValue) => {
  // Find the hour in availableHours to check its status
  const hourData = availableHours.value.find(h => h.value === hourValue)
  
  // Prevent toggling unavailable hours
  if (hourData && !hourData.available) {
    console.log('[Booking Add] Cannot select unavailable hour:', hourValue)
    return
  }
  
  // Prevent toggling booked hours
  if (hourData && hourData.booked) {
    console.log('[Booking Add] Cannot toggle booked hour:', hourValue)
    return
  }
  
  const index = selectedHours.value.indexOf(hourValue)
  if (index === -1) {
    selectedHours.value.push(hourValue)
  } else {
    selectedHours.value.splice(index, 1)
  }
}

// Go to confirm page
const goToConfirm = () => {
  // Basic validation
  if (!bookingForm.teacherId) {
    ElMessage.warning(t('booking.selectTeacherFirst'))
    return
  }
  if (!bookingForm.customerId) {
    ElMessage.warning(t('booking.selectCustomer'))
    return
  }
  if (!bookingForm.date) {
    ElMessage.warning(t('booking.dateRequired'))
    return
  }
  if (selectedHours.value.length === 0) {
    ElMessage.warning(t('booking.selectAtLeastOneHour'))
    return
  }
  
  // VIP validation - MUST check before proceeding
  // CRITICAL: Non-VIP customers cannot book
  if (!vipInfo.hasVip) {
    ElMessage.error({
      message: t('booking.onlyVipCanBook'),
      duration: 4000
    })
    return
  }
  
  if (vipInfo.remainingHours <= 0) {
    ElMessage.error({
      message: t('booking.vipNoHours'),
      duration: 4000
    })
    return
  }
  
  const selectedHoursCount = selectedHours.value.length
  if (selectedHoursCount > vipInfo.remainingHours) {
    ElMessage.warning({
      message: `${t('booking.vipHoursExceed')} ${t('booking.remainingHours')}: ${vipInfo.remainingHours}, ${t('booking.selectedHours')}: ${selectedHoursCount}`,
      duration: 5000
    })
    return
  }
  
  // All validations passed, save data to store and navigate to confirm page
  const teacher = teachers.value.find(t => t.id === bookingForm.teacherId)
  const customer = customers.value.find(s => s.id === bookingForm.customerId)
  
  // Save booking data to store
  bookingStore.pendingBookingData = {
    teacherId: bookingForm.teacherId,
    customerId: bookingForm.customerId,
    date: bookingForm.date,
    hours: [...selectedHours.value],
    teacherName: teacher?.name || '',
    customerName: customer?.name || '',
    hasVip: vipInfo.hasVip,
    vipPackageName: vipInfo.packageName,
    remainingHours: vipInfo.remainingHours,
    totalHours: vipInfo.totalHours,
    lastUpdated: vipInfo.lastUpdated
  }
  
  router.push('/booking/confirm')
}

// Handle cancel
const handleCancel = () => {
  router.back()
}

onMounted(() => {
  loadTeachers()
  loadCustomers()
  loadWeeklyCalendarTemplates() // Load weekly calendar templates
  
  // Check if returning from confirm page with booking data
  const query = route.query
  console.log('[Booking Add] onMounted - route.query:', query)
  
  // Store query data for restoration after data loads
  if (query.teacherId || query.customerId) {
    pendingRestore.value = true
  }
})

// Watch for data to load and then restore selections
watch([teachers, customers], async () => {
  if (pendingRestore.value && teachers.value.length > 0 && customers.value.length > 0) {
    const query = route.query
    console.log('[Booking Add] Data loaded, restoring selections')
    console.log('[Booking Add] Teachers:', teachers.value)
    console.log('[Booking Add] Customers:', customers.value)
    console.log('[Booking Add] Query teacherId:', query.teacherId, 'type:', typeof query.teacherId)
    console.log('[Booking Add] Query customerId:', query.customerId, 'type:', typeof query.customerId)
    
    // Convert query params to proper types
    const teacherId = query.teacherId
    const customerId = query.customerId
    
    // Find matching teacher and customer to verify they exist
    const matchedTeacher = teachers.value.find(t => String(t.id) === String(teacherId))
    const matchedCustomer = customers.value.find(c => String(c.id) === String(customerId))
    
    console.log('[Booking Add] Matched teacher:', matchedTeacher)
    console.log('[Booking Add] Matched customer:', matchedCustomer)
    
    if (matchedTeacher) {
      bookingForm.teacherId = matchedTeacher.id
    }
    if (matchedCustomer) {
      bookingForm.customerId = matchedCustomer.id
    }
    if (query.date) {
      bookingForm.date = query.date
    }
    if (query.hours) {
      selectedHours.value = query.hours.split(',')
    }
    if (query.hasVip === 'true') {
      vipInfo.hasVip = true
      vipInfo.packageName = query.vipPackageName || ''
      vipInfo.remainingHours = Number(query.remainingHours) || 0
      vipInfo.totalHours = Number(query.totalHours) || 0
    }
    
    pendingRestore.value = false
    
    // Load available dates and schedule if teacher is selected
    if (bookingForm.teacherId) {
      console.log('[Booking Add] Loading dates and schedule for teacher:', bookingForm.teacherId)
      loadingSchedule.value = true
      
      try {
        // Fetch schedule and wait for it
        await scheduleStore.fetchWeeklySchedule(bookingForm.teacherId)
        console.log('[Booking Add] Schedule loaded for restored booking')
        loadAvailableDates()
        
        // Load available hours if date is selected (after schedule is loaded)
        if (bookingForm.date) {
          console.log('[Booking Add] Loading hours for restored date:', bookingForm.date)
          loadAvailableHours()
        }
      } catch (error) {
        console.error('[Booking Add] Failed to load schedule for restored booking:', error)
      } finally {
        loadingSchedule.value = false
      }
    }
  }
}, { immediate: true })
</script>

<style scoped lang="scss">
.add-booking-page {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px 16px;
  
  .page-header {
    margin-bottom: 20px;
    
    .page-title {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: #1f2329;
      text-align: center;
    }
  }
  
  .booking-form-card {
    border: none;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
    
    :deep(.el-card__body) {
      padding: 24px 20px;
    }
  }
  
  .form-section {
    margin-bottom: 24px;
    
    .field-label {
      display: block;
      font-size: 15px;
      font-weight: 500;
      color: #1f2329;
      margin-bottom: 12px;
      
      .required {
        color: #f56c6c;
        margin-left: 2px;
      }
    }
    
    .full-width {
      width: 100%;
    }
    
    .select-with-icon {
      :deep(.el-input__wrapper) {
        padding: 14px 18px;
        border-radius: 10px;
        box-shadow: 0 0 0 1px #dcdfe6 inset;
        transition: all 0.3s;
        
        &:hover {
          box-shadow: 0 0 0 1px #c0c4cc inset;
        }
        
        &.is-focus {
          box-shadow: 0 0 0 1px #409eff inset;
        }
      }
      
      :deep(.el-input__inner) {
        font-size: 16px;
        height: 24px;
      }
      
      .select-icon {
        color: #409eff;
        font-size: 22px;
        margin-right: 10px;
      }
      
      // Style dropdown options
      :deep(.el-select-dropdown__item) {
        height: 56px;
        line-height: normal;
        font-size: 15px;
        padding: 8px 20px;
        
        &.is-selected {
          font-weight: 600;
          color: #409eff;
        }
        
        &:hover {
          background: #ecf5ff;
        }
      }
      
      :deep(.el-select-dropdown__item span) {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      
      .option-content {
        display: flex;
        flex-direction: column;
        gap: 4px;
        width: 100%;
      }
      
      .option-name {
        font-size: 15px;
        font-weight: 500;
        color: #303133;
      }
      
      .option-phone {
        font-size: 13px;
        color: #909399;
      }
    }
    
    .available-dates-container {
      background: #f5f7fa;
      border-radius: 8px;
      padding: 16px;
      
      .dates-header {
        font-size: 14px;
        color: #606266;
        margin-bottom: 12px;
        font-weight: 500;
      }
      
      .available-dates {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 8px;
        
        .date-item {
          min-width: 70px;
          height: 70px;
          border-radius: 8px;
          background: #fff;
          border: 1px solid #e4e7ed;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s;
          
          &:hover {
            border-color: #409eff;
            background: #ecf5ff;
            transform: translateY(-2px);
          }
          
          &.is-selected {
            background: #409eff;
            border-color: #409eff;
            color: #fff;
            box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
            
            &:hover {
              transform: translateY(-2px);
            }
          }
          
          &.is-disabled {
            opacity: 0.5;
            cursor: not-allowed;
            background: #f5f7fa;
            
            &:hover {
              transform: none;
            }
          }
          
          .date-value {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 4px;
          }
          
          .date-weekday {
            font-size: 13px;
            opacity: 0.8;
          }
        }
      }
    }
    
    .hours-grid {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 12px;
      margin-bottom: 8px;
      
      .hour-item {
        aspect-ratio: 1;
        border-radius: 8px;
        background: #f5f7fa;
        border: 1px solid #e4e7ed;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s;
        position: relative;
        
        .hour-label {
          font-size: 15px;
          color: #606266;
          font-weight: 500;
        }
        
        &:hover {
          border-color: #409eff;
          background: #ecf5ff;
          transform: translateY(-2px);
        }
        
        &.is-selected {
          background: #409eff;
          border-color: #409eff;
          
          .hour-label {
            color: #fff;
          }
          
          &:hover {
            transform: translateY(-2px);
          }
        }
        
        &.is-disabled {
          opacity: 0.4;
          cursor: not-allowed;
          background: #f5f7fa;
          
          &:hover {
            transform: none;
          }
          
          .hour-label {
            text-decoration: line-through;
          }
        }
        
        &.is-booked {
          background: #e6a23c;
          border-color: #e6a23c;
          cursor: not-allowed;
          
          .hour-label {
            color: #fff;
            font-weight: 600;
          }
          
          &:hover {
            transform: none;
          }
        }
        
        .unavailable-icon {
          position: absolute;
          top: 4px;
          right: 4px;
          font-size: 14px;
          color: #f56c6c;
        }
        
        .booked-icon {
          position: absolute;
          top: 4px;
          right: 4px;
          font-size: 14px;
          color: #fff;
        }
      }
    }
    
    .hours-hint {
      font-size: 13px;
      color: #909399;
      margin-top: 8px;
    }
    
    .vip-info-display {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
      background: #f5f7fa;
      border-radius: 8px;
      border: 2px solid transparent;
      transition: all 0.3s;
      
      &.vip-warning {
        background: #fdf6ec;
        border-color: #faecd8;
      }
      
      &.vip-error {
        background: #fef0f0;
        border-color: #fde2e2;
      }
      
      &.vip-blocked {
        background: #fef0f0;
        border-color: #f56c6c;
      }
      
      .vip-icon {
        font-size: 24px;
        color: #f5a623;
      }
      
      .vip-info-content {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
        
        .vip-status {
          font-size: 15px;
          font-weight: 500;
          color: #606266;
        }
        
        .vip-hours {
          font-size: 14px;
          color: #909399;
        }
        
        .vip-last-updated {
          font-size: 12px;
          color: #c0c4cc;
          margin-top: 2px;
        }
        
        .vip-warning-text {
          font-size: 13px;
          color: #f56c6c;
          font-weight: 500;
          margin-top: 4px;
        }
      }
    }
    
    .fee-display {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 16px;
      background: #f5f7fa;
      border-radius: 8px;
      
      .fee-icon {
        font-size: 20px;
        color: #f5a623;
      }
      
      .fee-amount {
        font-size: 20px;
        font-weight: 600;
        color: #f5a623;
      }
      
      .fee-detail {
        font-size: 13px;
        color: #909399;
        margin-left: auto;
      }
    }
  }
  
  .form-actions {
    margin-top: 32px;
    display: flex;
    flex-direction: row;
    gap: 12px;
    justify-content: flex-end;
    
    .confirm-btn {
      width: 100%;
      height: 48px;
      font-size: 16px;
      font-weight: 500;
      background: linear-gradient(90deg, #409eff 0%, #66b1ff 100%);
      border: none;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
      
      &:hover:not(:disabled) {
        opacity: 0.9;
        transform: translateY(-2px);
      }
      
      &:disabled {
        background: #dcdfe6;
        box-shadow: none;
        cursor: not-allowed;
      }
      
      .el-icon {
        margin-right: 8px;
      }
    }
    
    .cancel-btn {
      width: 100%;
      height: 48px;
      font-size: 16px;
      background: #fff;
      border: 1px solid #dcdfe6;
      border-radius: 8px;
      color: #606266;
      
      &:hover {
        border-color: #c0c4cc;
        color: #909399;
      }
    }
  }
}

// Responsive design
@media (max-width: 480px) {
  .add-booking-page {
    padding: 16px 12px;
    
    .booking-form-card {
      :deep(.el-card__body) {
        padding: 20px 16px;
      }
    }
    
    .available-dates-container {
      .available-dates {
        grid-template-columns: repeat(3, 1fr);
        
        .date-item {
          min-width: 60px;
          height: 60px;
          
          .date-value {
            font-size: 14px;
          }
          
          .date-weekday {
            font-size: 12px;
          }
        }
      }
    }
    
    .hours-grid {
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      
      .hour-item {
        .hour-label {
          font-size: 14px;
        }
      }
    }
  }
}
</style>